import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Save, Plus, Upload, Calendar } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import { NOTE_TOPICS, sujetDeNote } from '../constants/noteTopics';
import { grouperComptes } from '../constants/plateformes';
import { aujourdhuiIso } from '../utils/periodUtils';

const API_URL = config.API_URL;

/**
 * Les trois façons de documenter une période, réunies sous la modale.
 *
 * Elles existaient déjà, mais seulement dans le menu latéral : une fois la modale ouverte,
 * celui qui venait pour verser un tableau de trente opérations n'avait d'autre choix que de
 * refermer et de repartir à la recherche du menu. Les onglets les mettent côte à côte au
 * moment où le besoin se présente.
 *
 * Seul le premier est un formulaire. Le calendrier et l'import sont des **vues** : leur
 * onglet porte une `vue`, et un onglet qui en porte une referme la modale et navigue
 * (`onOuvrirVue`) au lieu de changer son contenu. Les y loger aurait voulu dire deux écrans
 * à la fois dans 900 pixels, pour des gestes qui ont chacun leur propre page.
 */
const ONGLETS = [
  { cle: 'note', libelle: 'Nouvelle note', Icone: Plus },
  { cle: 'import', libelle: 'Importer des notes', Icone: Upload, vue: 'notes-import' },
  { cle: 'calendrier', libelle: 'Ajouter au calendrier', Icone: Calendar, vue: 'notes-calendrier' }
];

const NotesModal = ({
  isOpen,
  onClose,
  onSave,
  editingNote = null,
  currentContext = {},
  clients = [],
  sites = [],
  accounts = [],
  token,
  onOuvrirVue = null
}) => {
  // State du formulaire
  const [content, setContent] = useState('');
  const [clientId, setClientId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [platform, setPlatform] = useState('');
  const [accountId, setAccountId] = useState('');
  // Sujet de la note. Vide vaut « aucun sujet », un état légitime : les notes déjà en base
  // n'en ont pas non plus tant qu'on ne les a pas reprises.
  const [topic, setTopic] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showOnChart, setShowOnChart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /**
   * Les onglets ne s'affichent qu'à la création — modifier une note existante n'a rien à voir
   * avec un import — et seulement là où la modale peut naviguer, c'est-à-dire quand
   * l'appelant fournit `onOuvrirVue`. Sans lui, deux des trois onglets seraient inertes.
   */
  const avecOnglets = !editingNote && typeof onOuvrirVue === 'function';

  /**
   * Le contexte et les comptes, lus sans être écoutés.
   *
   * L'effet ci-dessous n'a qu'un rôle : **remplir le formulaire à l'ouverture**. Le faire
   * dépendre de l'identité de `currentContext` le rejouait à chaque rendu de la modale — et
   * `currentContext = {}` par défaut fabrique un objet neuf à chaque appel du composant. Un
   * appelant qui ne passait pas la prop rendait donc le formulaire inutilisable : chaque
   * frappe déclenchait un rendu, l'effet se rejouait, et le champ revenait à sa valeur
   * d'origine. C'était le cas du calendrier.
   */
  const contexteRef = useRef(currentContext);
  contexteRef.current = currentContext;
  const comptesRef = useRef(accounts);
  comptesRef.current = accounts;

  // Initialiser le formulaire selon le contexte ou la note en édition
  useEffect(() => {
    const currentContext = contexteRef.current || {};
    const accounts = comptesRef.current;
    if (editingNote) {
      setContent(editingNote.content || '');
      setClientId(editingNote.clientId || '');
      setSiteId(editingNote.siteId || '');
      setPlatform(editingNote.platform || '');
      setAccountId(editingNote.accountId || '');
      setTopic(editingNote.topic || '');
      setStartDate(editingNote.startDate ? editingNote.startDate.split('T')[0] : '');
      setEndDate(editingNote.endDate ? editingNote.endDate.split('T')[0] : '');
      setShowOnChart(editingNote.showOnChart !== false);
    } else {
      setContent('');
      setClientId(currentContext.clientId || '');
      setPlatform(currentContext.platform || '');
      setAccountId(currentContext.accountId || '');
      setTopic('');

      // Si un compte est pré-sélectionné, trouver son site correspondant
      if (currentContext.accountId && Array.isArray(accounts)) {
        const account = accounts.find(a => a.sourceId === currentContext.accountId);
        if (account && account.siteId) {
          setSiteId(account.siteId);
        } else {
          setSiteId(currentContext.siteId || '');
        }
      } else {
        setSiteId(currentContext.siteId || '');
      }

      const today = aujourdhuiIso();
      setStartDate(today);
      setEndDate(today);
      setShowOnChart(false);
    }
    setError(null);
    setSuccess(false);
    // Volontairement limité à ce qui doit **réinitialiser** le formulaire : la note éditée
    // et l'ouverture. Le contexte et les comptes sont lus par référence, ci-dessus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingNote, isOpen]);

  // Sites filtrés par client sélectionné
  const filteredSites = useMemo(() => {
    const sitesList = Array.isArray(sites) ? sites : [];
    if (!clientId) return sitesList;
    return sitesList.filter(s => s.clientId === clientId);
  }, [sites, clientId]);

  // Comptes filtrés par client et/ou site sélectionné
  const filteredAccounts = useMemo(() => {
    const accountsList = Array.isArray(accounts) ? accounts : [];
    const sitesList = Array.isArray(sites) ? sites : [];

    if (siteId) {
      return accountsList.filter(a => a.siteId === siteId);
    }

    if (clientId) {
      const clientSiteIds = sitesList.filter(s => s.clientId === clientId).map(s => s.id);
      return accountsList.filter(a => clientSiteIds.includes(a.siteId));
    }

    return accountsList;
  }, [accounts, sites, clientId, siteId]);

  // Reset site et compte si le client change + auto-select si 1 seul site
  useEffect(() => {
    if (!clientId) return;
    const sitesList = Array.isArray(sites) ? sites : [];
    const accountsList = Array.isArray(accounts) ? accounts : [];

    const clientSites = sitesList.filter(s => s.clientId === clientId);

    // Si le client n'a qu'un seul site, le sélectionner automatiquement
    if (clientSites.length === 1 && !siteId) {
      setSiteId(clientSites[0].id);
    }

    // Si le site sélectionné n'appartient pas au client, le réinitialiser
    const siteInClient = clientSites.find(s => s.id === siteId);
    if (siteId && !siteInClient) {
      // Si 1 seul site, le sélectionner, sinon vider
      if (clientSites.length === 1) {
        setSiteId(clientSites[0].id);
      } else {
        setSiteId('');
      }
    }

    const clientSiteIds = clientSites.map(s => s.id);
    const accountInClient = accountsList.find(a => a.sourceId === accountId && clientSiteIds.includes(a.siteId));
    if (accountId && !accountInClient) {
      setAccountId('');
      setPlatform('');
    }
  }, [clientId, siteId, accountId, sites, accounts]);

  // Reset compte si le site change
  useEffect(() => {
    if (!siteId) return;
    const accountsList = Array.isArray(accounts) ? accounts : [];
    const accountInSite = accountsList.find(a => a.sourceId === accountId && a.siteId === siteId);
    if (accountId && !accountInSite) {
      setAccountId('');
      setPlatform('');
    }
  }, [siteId, accountId, accounts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!content.trim()) {
      setError('Le contenu de la note est requis');
      return;
    }

    if (!startDate) {
      setError('La date est requise');
      return;
    }

    setSaving(true);

    try {
      const noteData = {
        content: content.trim(),
        clientId: clientId || null,
        siteId: siteId || null,
        platform: platform || null,
        accountId: accountId || null,
        // Toujours transmis, y compris vide : le serveur lit l'absence de clé comme « ne pas
        // toucher », si bien qu'un sujet retiré ne serait jamais retiré.
        topic: topic || null,
        startDate,
        endDate: endDate || null,
        showOnChart
      };

      let response;
      if (editingNote) {
        response = await axios.put(
          `${API_URL}/api/notes/${editingNote.id}`,
          noteData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${API_URL}/api/notes`,
          noteData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      if (response.data.success) {
        setSaving(false);
        setSuccess(true);
        // Attendre pour montrer la confirmation, puis fermer
        setTimeout(() => {
          onSave(response.data.note);
          setSuccess(false);
          onClose();
        }, 1500);
        return; // Ne pas exécuter finally
      } else {
        setError(response.data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde note:', err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const selectStyle = {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: 'white',
    minWidth: 0,
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    flex: 1
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '4px',
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: 500
  };

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <div
        className="modal-content notes-modal"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '900px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          padding: 0,
          /* Sur un écran bas, l'en-tête et les onglets restent en place et seul le corps
             défile — sans quoi le bouton d'enregistrement sortait de la fenêtre. */
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header crème */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          backgroundColor: '#FAF9F5',
          borderBottom: '1px solid #e5e7eb',
          borderRadius: '12px 12px 0 0'
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
            {editingNote ? 'Modifier la note' : 'Nouvelle note'}
          </h3>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Onglets : les trois façons d'entrer des notes, à l'endroit où on les cherche. */}
        {avecOnglets && (
          <div style={{
            display: 'flex',
            gap: '4px',
            padding: '0 20px',
            backgroundColor: '#FAF9F5',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0
          }}>
            {ONGLETS.map(({ cle, libelle, Icone, vue }) => {
              const actif = !vue;
              return (
                <button
                  key={cle}
                  type="button"
                  // Un onglet porteur de vue referme la modale et y mène : l'appelant sait
                  // naviguer, la modale non.
                  onClick={() => { if (vue) onOuvrirVue(vue); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 14px',
                    border: 'none',
                    borderBottom: `2px solid ${actif ? '#1a1a1a' : 'transparent'}`,
                    backgroundColor: 'transparent',
                    fontSize: '13px',
                    fontWeight: actif ? 600 : 500,
                    color: actif ? '#111827' : '#6b7280',
                    cursor: 'pointer',
                    marginBottom: '-1px'
                  }}
                >
                  <Icone size={15} />
                  <span>{libelle}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px 24px' }}>
            {error && (
              <div style={{
                padding: '10px 14px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                marginBottom: '12px',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            {success ? (
              <div style={{
                padding: '40px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ fontSize: '28px', color: 'white' }}>✓</span>
                </div>
                <span style={{ fontSize: '16px', fontWeight: 500, color: '#166534' }}>
                  Note enregistrée avec succès
                </span>
              </div>
            ) : (
              <>
            {/* Layout horizontal: Note + Filtres */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
              {/* Colonne gauche: Note */}
              <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Sujet. Au-dessus du champ de saisie, et non en bas de la colonne de portée :
                    c'est le premier choix à faire — il dit de quoi la note parle, là où les
                    champs de droite disent à quoi elle se rattache. Rangé parmi eux, il se
                    lisait comme un filtre de plus et restait vide. Sans libellé devant : la
                    liste porte déjà sa consigne, la répéter ne dirait rien de plus. */}
                <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <select
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    style={{
                      ...selectStyle,
                      // Le cadre d'attention est sur le sujet, et non sur la zone de texte :
                      // c'est le premier des deux à renseigner, et c'est celui qu'on oublie.
                      // Une fois choisi, le cadre prend sa couleur et cesse d'appeler.
                      border: `1px solid ${sujetDeNote(topic)?.couleur || '#7c2d12'}`,
                      color: sujetDeNote(topic)?.couleur || '#374151'
                    }}
                  >
                    {/* Placeholder, et non une valeur affichée : la note enregistrée porte bien
                        `topic: null`, que l'archive distingue toujours d'« Autre ». */}
                    <option value="">Choisir un sujet</option>
                    {NOTE_TOPICS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Opération commerciale, bug, événement..."
                  style={{
                    width: '100%',
                    height: '150px',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    resize: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Colonne droite: Portée.
                  `minWidth: 0` sur la colonne et sur chaque ligne, sans quoi la moitié
                  n'est qu'un souhait : un élément flex ne descend pas sous la largeur
                  minimale de son contenu, et un `<select>` la tient de sa plus longue
                  option. Un nom de compte à rallonge poussait la colonne bien au-delà des
                  50 % et écrasait la saisie de la note. Les options, elles, sont tronquées
                  par `text-overflow` plutôt que de dicter la largeur. */}
              <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Client */}
                {clients.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500, minWidth: '60px' }}>Client</span>
                    <select
                      value={clientId}
                      onChange={e => setClientId(e.target.value)}
                      style={{ ...selectStyle }}
                    >
                      <option value="">Tous les clients</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Site */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500, minWidth: '60px' }}>Site</span>
                  <select
                    value={siteId}
                    onChange={e => setSiteId(e.target.value)}
                    style={{ ...selectStyle }}
                  >
                    <option value="">Tous les sites</option>
                    {Array.isArray(filteredSites) && filteredSites.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                {/* Compte */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500, minWidth: '60px' }}>Compte</span>
                  <select
                    value={accountId}
                    onChange={e => {
                      setAccountId(e.target.value);
                      const selectedAccount = filteredAccounts.find(a => a.sourceId === e.target.value);
                      setPlatform(selectedAccount ? selectedAccount.type : '');
                    }}
                    style={{ ...selectStyle }}
                  >
                    <option value="">Tous les comptes</option>
                    {/* Regroupés par plateforme : la liste mêle régies et solutions de
                        mesure, et rien dans un nom de compte ne dit d'où il vient. */}
                    {grouperComptes(filteredAccounts).map(groupe => (
                      <optgroup key={groupe.type} label={groupe.libelle}>
                        {groupe.comptes.map(a => (
                          <option key={a.sourceId} value={a.sourceId}>{a.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Ligne inférieure: Dates + Toggle */}
            <div style={{
              display: 'flex',
              gap: '20px',
              alignItems: 'flex-end',
              padding: '16px 20px',
              backgroundColor: '#FAF9F5',
              borderRadius: '8px'
            }}>
              {/* Dates */}
              <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Debut</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Fin</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    min={startDate}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Toggle Graphe */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 12px',
                backgroundColor: showOnChart ? '#dcfce7' : '#f3f4f6',
                borderRadius: '6px',
                border: showOnChart ? '1px solid #bbf7d0' : '1px solid #e5e7eb'
              }}>
                <span
                  style={{ fontSize: '13px', color: '#374151', whiteSpace: 'nowrap', cursor: 'help' }}
                  title="Afficher cette note comme annotation sur les graphiques du dashboard"
                >
                  Afficher sur les graphiques
                </span>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '40px',
                  height: '22px'
                }}>
                  <input
                    type="checkbox"
                    checked={showOnChart}
                    onChange={e => setShowOnChart(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: showOnChart ? '#22c55e' : '#d1d5db',
                    borderRadius: '22px',
                    transition: '0.2s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      height: '16px',
                      width: '16px',
                      left: showOnChart ? '21px' : '3px',
                      bottom: '3px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: '0.2s',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }} />
                  </span>
                </label>
              </div>

              {/* Boutons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#374151',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving || !content.trim() || !startDate}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: saving || !content.trim() || !startDate ? '#9ca3af' : '#1a1a1a',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'white',
                    cursor: saving || !content.trim() || !startDate ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Save size={14} />
                  {saving ? '...' : 'Enregistrer'}
                </button>
              </div>
            </div>
              </>
            )}
          </div>
        </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotesModal;
